import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Sparkles, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { BILLING_LABELS, centsToEuro, ClausesEditor, euroToCents } from '@/components/contract-parts';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field } from '@/components/ui/form-field';
import { Alert, Badge, Card, Checkbox, Input, Select, Switch, Textarea } from '@/components/ui/primitives';
import { EmptyRow, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { BillingInterval, ContractClause, ContractTemplateItem, SlaPolicyItem, TicketMeta } from '@/lib/types';

const KEY = ['contract-templates'];
const onError = (e: unknown) => toast.error(errorMessage(e));

interface Form {
  name: string;
  type: string;
  title: string;
  description: string;
  services: string;
  areaIds: string[];
  slaPolicyId: string;
  termMonths: string;
  autoRenew: boolean;
  renewalMonths: number;
  noticeDays: string;
  price: string;
  billingInterval: BillingInterval | '';
  clauses: ContractClause[];
  active: boolean;
}

const empty: Form = {
  name: '',
  type: '',
  title: '',
  description: '',
  services: '',
  areaIds: [],
  slaPolicyId: '',
  termMonths: '12',
  autoRenew: true,
  renewalMonths: 12,
  noticeDays: '90',
  price: '',
  billingInterval: 'MONTHLY',
  clauses: [],
  active: true,
};

const fromTemplate = (t: ContractTemplateItem): Form => ({
  name: t.name,
  type: t.type,
  title: t.title,
  description: t.description ?? '',
  services: t.services.join('\n'),
  areaIds: t.areaIds,
  slaPolicyId: t.slaPolicyId ?? '',
  termMonths: t.termMonths?.toString() ?? '',
  autoRenew: t.autoRenew,
  renewalMonths: t.renewalMonths,
  noticeDays: t.noticeDays?.toString() ?? '',
  price: centsToEuro(t.priceCents),
  billingInterval: t.billingInterval ?? '',
  clauses: t.clauses,
  active: t.active,
});

export function TemplatesTab() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const canManage = can('contracts.manage');
  const { data } = useQuery({ queryKey: KEY, queryFn: () => api<{ items: ContractTemplateItem[] }>('/contract-templates') });
  const { data: meta } = useQuery({ queryKey: ['tickets', 'meta'], queryFn: () => api<TicketMeta>('/tickets/meta') });
  const { data: policies } = useQuery({ queryKey: ['sla-policies'], queryFn: () => api<SlaPolicyItem[]>('/sla-policies') });
  const [edit, setEdit] = useState<{ id: string | null; form: Form } | null>(null);
  const [form, setForm] = useState<Form>(empty);
  const [confirmDialog, confirm] = useConfirm();
  useEffect(() => {
    if (edit) setForm(edit.form);
  }, [edit]);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  const done = (r: { items: ContractTemplateItem[] }) => {
    qc.setQueryData(KEY, r);
    setEdit(null);
  };
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name,
        type: form.type,
        title: form.title,
        description: form.description || null,
        services: form.services.split('\n').map((s) => s.trim()).filter(Boolean),
        areaIds: form.areaIds,
        slaPolicyId: form.slaPolicyId || null,
        termMonths: form.termMonths ? Number(form.termMonths) : null,
        autoRenew: form.autoRenew,
        renewalMonths: form.renewalMonths,
        noticeDays: form.noticeDays ? Number(form.noticeDays) : null,
        priceCents: euroToCents(form.price),
        billingInterval: form.billingInterval || null,
        clauses: form.clauses.filter((c) => c.heading.trim() && c.text.trim()),
        active: form.active,
      };
      return edit!.id ? api<{ items: ContractTemplateItem[] }>(`/contract-templates/${edit!.id}`, { method: 'PUT', body }) : api<{ items: ContractTemplateItem[] }>('/contract-templates', { method: 'POST', body });
    },
    onSuccess: (r) => {
      done(r);
      toast.success('Vorlage gespeichert');
    },
    onError,
  });
  const sample = useMutation({
    mutationFn: () => api<{ items: ContractTemplateItem[] }>('/contract-templates/sample', { method: 'POST' }),
    onSuccess: (r) => {
      qc.setQueryData(KEY, r);
      toast.success('Mustervorlage angelegt – bitte prüfen und anpassen');
    },
    onError,
  });
  const del = useMutation({ mutationFn: (id: string) => api<{ items: ContractTemplateItem[] }>(`/contract-templates/${id}`, { method: 'DELETE' }), onSuccess: done, onError });

  return (
    <Card>
      {confirmDialog}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b p-3">
        <p className="text-sm text-muted-foreground">Vorlagen belegen neue Verträge vor – inklusive Vertragsbedingungen für das PDF. Änderungen wirken nur auf künftige Verträge.</p>
        {canManage && (
          <div className="flex gap-2">
            {!data?.items.some((t) => t.name.startsWith('Muster:')) && (
              <Button size="sm" variant="outline" onClick={() => sample.mutate()} loading={sample.isPending}>
                <Sparkles /> Mustervorlage
              </Button>
            )}
            <Button size="sm" onClick={() => setEdit({ id: null, form: empty })}>
              <Plus /> Vorlage
            </Button>
          </div>
        )}
      </div>
      <Table>
        <THead>
          <TR className="hover:bg-transparent">
            <TH>Vorlage</TH>
            <TH className="hidden md:table-cell">Laufzeit / Vergütung</TH>
            <TH className="hidden md:table-cell">Paragraphen</TH>
            <TH className="w-24" />
          </TR>
        </THead>
        <TBody>
          {data?.items.length === 0 && <EmptyRow colSpan={4}>Noch keine Vorlagen. Die Mustervorlage „IT-Wartungsvertrag“ ist ein guter Ausgangspunkt.</EmptyRow>}
          {data?.items.map((t) => (
            <TR key={t.id}>
              <TD>
                <p className="flex items-center gap-2 font-medium">
                  {t.name} {t.kind === 'DPA' && <Badge variant="outline">AVV</Badge>} {!t.active && <Badge variant="outline">inaktiv</Badge>}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t.type} · {t.contractCount} Verträge
                </p>
              </TD>
              <TD className="hidden text-sm md:table-cell">
                {t.termMonths ? `${t.termMonths} Monate` : 'unbefristet'}
                {t.priceCents != null && ` · ${centsToEuro(t.priceCents)} € ${t.billingInterval ? BILLING_LABELS[t.billingInterval] : ''}`}
              </TD>
              <TD className="hidden text-sm md:table-cell">{t.clauses.length}</TD>
              <TD className="text-right">
                {canManage && (
                  <>
                    <Button variant="ghost" size="icon" className="size-8" aria-label={`${t.name} bearbeiten`} onClick={() => setEdit({ id: t.id, form: fromTemplate(t) })}>
                      <Pencil />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8 text-destructive hover:text-destructive"
                      aria-label={`${t.name} löschen`}
                      onClick={async () => (await confirm({ title: `Vorlage „${t.name}“ löschen?`, description: 'Bestehende Verträge bleiben unverändert.', confirmLabel: 'Löschen', destructive: true })) && del.mutate(t.id)}
                    >
                      <Trash2 />
                    </Button>
                  </>
                )}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>

      <Dialog open={edit !== null} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent wide>
          <form
            className="flex min-h-0 flex-col"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
          >
            <DialogHeader>
              <DialogTitle>{edit?.id ? 'Vorlage bearbeiten' : 'Neue Vertragsvorlage'}</DialogTitle>
              <DialogDescription>Platzhalter in den Vertragsbedingungen werden beim Erzeugen des PDFs mit den Daten des jeweiligen Vertrags gefüllt.</DialogDescription>
            </DialogHeader>
            <DialogBody className="grid gap-5">
              {form.name.startsWith('Muster:') && <Alert>Mustertext ohne Gewähr – vor dem Einsatz an Ihr Angebot anpassen und rechtlich prüfen lassen.</Alert>}
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Name der Vorlage" htmlFor="t-name">
                  <Input id="t-name" required value={form.name} onChange={(e) => set('name', e.target.value)} />
                </Field>
                <Field label="Vertragsbezeichnung" htmlFor="t-title">
                  <Input id="t-title" required value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="z. B. IT-Wartungsvertrag" />
                </Field>
                <Field label="Vertragsart" htmlFor="t-type">
                  <Input id="t-type" required value={form.type} onChange={(e) => set('type', e.target.value)} placeholder="z. B. IT-Wartung" />
                </Field>
                <Field label="Laufzeit (Monate, leer = unbefristet)" htmlFor="t-term">
                  <Input id="t-term" type="number" min={1} value={form.termMonths} onChange={(e) => set('termMonths', e.target.value)} />
                </Field>
                <Field label="Kündigungsfrist (Tage)" htmlFor="t-notice">
                  <Input id="t-notice" type="number" min={0} value={form.noticeDays} onChange={(e) => set('noticeDays', e.target.value)} />
                </Field>
                <label className="flex items-center gap-2 self-end rounded-md border p-2.5 text-sm">
                  <Switch checked={form.autoRenew} onCheckedChange={(v) => set('autoRenew', v)} /> verlängert sich um
                  <Input type="number" className="w-16" min={1} value={form.renewalMonths} onChange={(e) => set('renewalMonths', Number(e.target.value))} aria-label="Verlängerung in Monaten" /> Mon.
                </label>
                <Field label="Vergütung (netto, €)" htmlFor="t-price">
                  <Input id="t-price" inputMode="decimal" value={form.price} onChange={(e) => set('price', e.target.value)} />
                </Field>
                <Field label="Intervall" htmlFor="t-int">
                  <Select id="t-int" value={form.billingInterval} onChange={(e) => set('billingInterval', e.target.value as Form['billingInterval'])}>
                    <option value="">–</option>
                    {(Object.keys(BILLING_LABELS) as BillingInterval[]).map((k) => (
                      <option key={k} value={k}>
                        {BILLING_LABELS[k]}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="SLA-Richtlinie" htmlFor="t-sla">
                  <Select id="t-sla" value={form.slaPolicyId} onChange={(e) => set('slaPolicyId', e.target.value)}>
                    <option value="">– Standard –</option>
                    {policies?.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
              <fieldset className="grid gap-2">
                <legend className="mb-1 text-sm font-medium">Ticketberechtigungen (Bereiche)</legend>
                <div className="flex flex-wrap gap-3">
                  {meta?.areas.filter((a) => a.active).map((a) => (
                    <label key={a.id} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={form.areaIds.includes(a.id)} onCheckedChange={() => set('areaIds', form.areaIds.includes(a.id) ? form.areaIds.filter((x) => x !== a.id) : [...form.areaIds, a.id])} />
                      {a.name}
                    </label>
                  ))}
                </div>
              </fieldset>
              <Field label="Leistungen (eine pro Zeile)" htmlFor="t-services">
                <Textarea id="t-services" rows={4} value={form.services} onChange={(e) => set('services', e.target.value)} />
              </Field>
              <fieldset className="grid gap-2">
                <legend className="mb-1 text-sm font-medium">Vertragsbedingungen</legend>
                <ClausesEditor value={form.clauses} onChange={(v) => set('clauses', v)} />
              </fieldset>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.active} onCheckedChange={(v) => set('active', v)} /> Vorlage aktiv (bei neuen Verträgen wählbar)
              </label>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEdit(null)}>
                Abbrechen
              </Button>
              <Button type="submit" loading={save.isPending} disabled={!form.name || !form.title || !form.type}>
                Speichern
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
